import { type ProfileServices_userProfile$key } from '#app/__generated__/relay/ProfileServices_userProfile.graphql.js';
import { type ProfileServicesReorderMutation } from '#app/__generated__/relay/ProfileServicesReorderMutation.graphql.js';
import { ProfileMagicBannerCTA } from '#app/components/ContraMagic/ProfileMagicBannerCTA/ProfileMagicBannerCTA.js';
import { animated } from '#app/components/Primitives/Animated.js';
import { useSortableListView } from '#app/components/SortableListView/hooks/useSortableListView.js';
import { SortableListItemWithClonedElement } from '#app/components/SortableListView/SortableListItemWithClonedElement.js';
import { SortableListView } from '#app/components/SortableListView/SortableListView.js';
import { ProductizedServiceCardDataWrapper } from '#app/components/UserProfile/ProductizedServiceCard/ProductizedServiceCardDataWrapper.js';
import {
  STAGGERED_CHILD_VARIANTS,
  STAGGERED_CHILDREN_CONTAINER_VARIANTS,
} from '#app/features/profile/utils/animationUtilities.js';
import { formatName } from '#app/utilities/formatName.js';
import { BlankCardMessage } from '../BlankCardMessage/BlankCardMessage.js';
import { CreateCard } from '../CreateCard/CreateCard.js';
import {
  ProfileServicesWrapper,
  ServiceCards,
} from './ProfileServices.styles.js';
// disable useContraMutation eslint error
import { graphql, useFragment, useMutation } from '@contra/relay';
import { type UniqueIdentifier } from '@dnd-kit/core';
import { useCallback, useMemo } from 'react';

type ProfileServicesProps = {
  readonly profileKey: ProfileServices_userProfile$key;
};

export const ProfileServices = ({ profileKey }: ProfileServicesProps) => {
  const profile = useFragment<ProfileServices_userProfile$key>(
    graphql`
      fragment ProfileServices_userProfile on UserProfile {
        id
        displayUsername
        visitorCanEdit
        firstName
        onboardingType
        productizedServices {
          count
          edges {
            node {
              id
              ...ProductizedServiceCardDataWrapper
            }
          }
        }
      }
    `,
    profileKey,
  );

  const [updateServiceOrder] = useMutation<ProfileServicesReorderMutation>(
    graphql`
      mutation ProfileServicesReorderMutation(
        $input: ReorderProductizedServicesInput!
      ) {
        reorderProductizedServices(input: $input) {
          errors {
            message
          }
          userProfile {
            ...ProfileServices_userProfile
          }
        }
      }
    `,
  );

  const onServiceIdsSorted = useCallback(
    (ids: UniqueIdentifier[]) => {
      updateServiceOrder({
        variables: {
          input: {
            productizedServices: ids.map((id) => id.toString()),
          },
        },
      });
    },
    [updateServiceOrder],
  );

  const servicesData = useMemo(() => {
    return profile?.productizedServices.edges.map(({ node }) => node) ?? [];
  }, [profile]);

  const serviceCount = servicesData.length;

  const hasServices = serviceCount > 0;

  // evaluate certain conditions when we shouldn't permit sorting of service cards.
  const shouldDisableSorting =
    // less than two services total
    serviceCount < 2 ||
    // doesn't have permission to edit services
    !profile.visitorCanEdit;

  const sortState = useSortableListView({
    data: servicesData,
    disabled: shouldDisableSorting,
    onDataIdsSorted: onServiceIdsSorted,
  });

  const {
    activeSortId,
    disabled,
    sortedData,
    sortedNodeIds,
    ...sortListProps
  } = sortState;

  const { onboardingType, visitorCanEdit } = profile;

  if (!sortedData || !sortedNodeIds) return null;

  return (
    <ProfileServicesWrapper
      noMarginTop
      visitorCanEdit={visitorCanEdit}
    >
      {!hasServices && !visitorCanEdit ? (
        <BlankCardMessage>
          {formatName({ firstName: profile.firstName }, 'firstName')} hasn't
          added any services yet
        </BlankCardMessage>
      ) : null}

      {visitorCanEdit && profile?.productizedServices?.count === 0 ? (
        <ProfileMagicBannerCTA
          displayUsername={profile.displayUsername}
          variant="service"
          visitorCanEdit={profile.visitorCanEdit}
        />
      ) : null}

      {profile.visitorCanEdit ? (
        <CreateCard
          variant={
            onboardingType === 'CONTRACTOR' ? 'service' : 'client-service'
          }
        />
      ) : null}

      <ServiceCards
        animate="show"
        exit="hidden"
        initial="hidden"
        variants={STAGGERED_CHILDREN_CONTAINER_VARIANTS}
        withMarginTop={visitorCanEdit}
      >
        <SortableListView
          sortedNodeIds={sortedNodeIds}
          sortStrategy="VERTICAL_LIST"
          {...sortListProps}
        >
          {sortedData.map((node, index) => {
            return (
              <animated.div
                key={node.id}
                variants={STAGGERED_CHILD_VARIANTS}
              >
                <SortableListItemWithClonedElement
                  activeSortId={activeSortId}
                  disabled={disabled}
                  sortId={node.id}
                >
                  {/* Sortable list while clone its child elements
                      to add some props that are specific to sorting. */}
                  <ProductizedServiceCardDataWrapper
                    canEdit={visitorCanEdit}
                    disabled={disabled}
                    isLastElement={index === sortedData.length - 1}
                    key={node.id}
                    position={index}
                    productizedService={node}
                    // there is some weird overlap with cards and the overflow menu when we use bottom
                    tagOverflowMenuPlacement="top"
                  />
                </SortableListItemWithClonedElement>
              </animated.div>
            );
          })}
        </SortableListView>
      </ServiceCards>
    </ProfileServicesWrapper>
  );
};
